// =============================================================================
// SHANIZ GAMING ADMIN - DASHBOARD
// =============================================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Stack,
  Chip,
  Skeleton,
  Avatar,
  Divider,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  SportsEsports as GamesIcon,
  EmojiEvents as TournamentIcon,
  AccountBalance as MoneyIcon,
  TrendingUp,
  TrendingDown,
  VerifiedUser as KycIcon,
  Payments as PayoutIcon,
  Gavel as DisputeIcon,
  SupportAgent as SupportIcon,
  Shield as FraudIcon,
  Bolt as BoltIcon,
  ArrowForward,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { dashboardService, statsService, auditService } from '../services/api';
import { CHART_SERIES, chartTokens, tooltipProps } from '../theme/chartTheme';

// -----------------------------------------------------------------------------

const KES = (v?: number | null) =>
  typeof v === 'number' ? `KES ${v.toLocaleString('en-KE')}` : 'KES —';

const compact = (v?: number | null) => {
  if (v == null) return '—';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'k';
  return v.toLocaleString('en-KE');
};

const timeAgo = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

// -----------------------------------------------------------------------------
// KPI card
// -----------------------------------------------------------------------------

interface KpiProps {
  label: string;
  value: string | number;
  delta?: { value: number; label: string } | null;
  icon: React.ReactNode;
  tone: 'primary' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
  loading?: boolean;
}

const Kpi: React.FC<KpiProps> = ({ label, value, delta, icon, tone, onClick, loading }) => {
  const theme = useTheme();
  const toneColor = theme.palette[tone].main;
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea disabled={!onClick} onClick={onClick} sx={{ height: '100%', p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${toneColor}1a`,
              color: toneColor,
            }}
          >
            {icon}
          </Box>
        </Stack>
        {loading ? (
          <Skeleton width={120} height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: '-0.02em' }}>
            {value}
          </Typography>
        )}
        {delta && !loading && (
          <Stack direction="row" alignItems="center" spacing={0.75} mt={1}>
            {delta.value >= 0 ? (
              <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography variant="caption" sx={{ color: delta.value >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
              {delta.value >= 0 ? '+' : ''}{delta.value}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {delta.label}
            </Typography>
          </Stack>
        )}
      </CardActionArea>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Pending-work action card
// -----------------------------------------------------------------------------

interface PendingCardProps {
  label: string;
  count: number;
  icon: React.ReactNode;
  tone: 'warning' | 'error' | 'info' | 'primary';
  onClick: () => void;
  description: string;
}

const PendingCard: React.FC<PendingCardProps> = ({ label, count, icon, tone, onClick, description }) => {
  const theme = useTheme();
  const toneColor = theme.palette[tone].main;
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ p: 2.5, height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${toneColor}1a`,
              color: toneColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
                {count}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {label}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, mt: 0.25 }}>
              {description}
            </Typography>
          </Box>
          <ArrowForward sx={{ color: 'text.secondary', fontSize: 18 }} />
        </Stack>
      </CardActionArea>
    </Card>
  );
};

// -----------------------------------------------------------------------------
// Main dashboard
// -----------------------------------------------------------------------------

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const ct = chartTokens(theme);
  const tt = tooltipProps(theme);

  const stats = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardService.getStats(),
  });

  const overview = useQuery({
    queryKey: ['admin', 'stats', 'overview'],
    queryFn: () => statsService.overview(),
  });

  const activity = useQuery({
    queryKey: ['admin', 'audit-logs', 'recent'],
    queryFn: () => auditService.getAll(1, 8),
  });

  const revenue = useQuery({
    queryKey: ['admin', 'stats', 'revenue', 7],
    queryFn: () => statsService.revenueSeries(7),
  });

  const growth = useQuery({
    queryKey: ['admin', 'stats', 'user-growth', 14],
    queryFn: () => statsService.userGrowth(14),
  });

  const loadingStats = stats.isLoading || overview.isLoading;
  const s: any = stats.data || {};
  const o: any = overview.data || {};

  // Shape revenue series for AreaChart (day label + revenue value)
  const revenueSeries = React.useMemo(() => {
    const rows: any[] = Array.isArray(revenue.data) ? revenue.data : [];
    if (rows.length === 0) {
      const now = new Date();
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (6 - i));
        return { day: date.toLocaleDateString('en-KE', { weekday: 'short' }), revenue: 0 };
      });
    }
    return rows.map((r) => ({
      day: new Date(r.date).toLocaleDateString('en-KE', { weekday: 'short' }),
      revenue: Number(r.revenue) || 0,
    }));
  }, [revenue.data]);

  const userGrowthSeries = React.useMemo(() => {
    const rows: any[] = Array.isArray(growth.data) ? growth.data : [];
    if (rows.length === 0) {
      const now = new Date();
      return Array.from({ length: 14 }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (13 - i));
        return { day: date.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' }), users: 0 };
      });
    }
    return rows.map((r) => ({
      day: new Date(r.date).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' }),
      users: Number(r.newUsers ?? r.users) || 0,
    }));
  }, [growth.data]);

  const pendingKyc = o?.moderation?.pendingKyc ?? o?.pending?.kyc ?? 0;
  const pendingPayouts = o?.moderation?.pendingPayouts ?? o?.pending?.payouts ?? 0;
  const pendingTickets = o?.moderation?.openTickets ?? o?.pending?.tickets ?? 0;
  const pendingDisputes = o?.moderation?.disputes ?? o?.pending?.disputes ?? 0;
  const revenueWeek = o?.revenue?.last7d ?? o?.revenue?.week ?? null;

  return (
    <Box sx={{ p: { xs: 1, md: 0 } }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary', letterSpacing: '-0.02em' }}>
            Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Overview of platform activity and pending admin work
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<BoltIcon sx={{ fontSize: 16 }} />}
            label="Live"
            size="small"
            sx={{
              bgcolor: 'success.main',
              color: 'common.white',
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'common.white' },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/analytics')}
            sx={{ textTransform: 'none' }}
          >
            Analytics →
          </Button>
        </Stack>
      </Stack>

      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Total Users"
            value={compact(s?.totalUsers ?? o?.users?.total ?? o?.users?.mau)}
            delta={{ value: 12, label: 'this week' }}
            icon={<PeopleIcon fontSize="small" />}
            tone="primary"
            onClick={() => navigate('/users')}
            loading={loadingStats}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Active Games"
            value={compact(s?.gamesInProgress ?? o?.games?.active)}
            delta={{ value: -3, label: 'vs yesterday' }}
            icon={<GamesIcon fontSize="small" />}
            tone="success"
            onClick={() => navigate('/games')}
            loading={loadingStats}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Active Tournaments"
            value={compact(s?.activeTournaments)}
            delta={{ value: 8, label: 'this week' }}
            icon={<TournamentIcon fontSize="small" />}
            tone="info"
            onClick={() => navigate('/tournaments')}
            loading={loadingStats}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Revenue Today"
            value={KES(s?.todayRevenue ?? o?.revenue?.today)}
            delta={{ value: 15, label: 'vs yesterday' }}
            icon={<MoneyIcon fontSize="small" />}
            tone="warning"
            onClick={() => navigate('/transactions')}
            loading={loadingStats}
          />
        </Grid>
      </Grid>

      {/* Pending work */}
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 1.5 }}>
        Needs Attention
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <PendingCard
            label="KYC Pending"
            count={pendingKyc}
            description="Awaiting verification review"
            icon={<KycIcon />}
            tone="warning"
            onClick={() => navigate('/kyc')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PendingCard
            label="Payouts"
            count={pendingPayouts}
            description="Ready for admin approval"
            icon={<PayoutIcon />}
            tone="error"
            onClick={() => navigate('/payouts')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PendingCard
            label="Open Tickets"
            count={pendingTickets}
            description="Customer support queue"
            icon={<SupportIcon />}
            tone="info"
            onClick={() => navigate('/support')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PendingCard
            label="Disputes"
            count={pendingDisputes}
            description="Match / payment disputes"
            icon={<DisputeIcon />}
            tone="primary"
            onClick={() => navigate('/fraud')}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Weekly Revenue
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Last 7 days, platform fees + subscriptions
                  </Typography>
                </Box>
                <Chip label={KES(revenueWeek)} size="small" sx={{ fontWeight: 600 }} />
              </Stack>
              <Box sx={{ height: 260, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                    <XAxis dataKey="day" stroke={ct.axisLine} tick={{ fontSize: 12, fill: ct.axisTick }} />
                    <YAxis stroke={ct.axisLine} tick={{ fontSize: 12, fill: ct.axisTick }} tickFormatter={(v) => compact(v)} />
                    <RechartsTooltip {...tt} />
                    <Line type="monotone" dataKey="revenue" stroke={CHART_SERIES[0]} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    User Growth
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Last 14 days, new signups
                  </Typography>
                </Box>
                <Chip label={`DAU ${compact((o as any)?.users?.dau)}`} size="small" color="primary" sx={{ fontWeight: 600 }} />
              </Stack>
              <Box sx={{ height: 260, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowthSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                    <XAxis dataKey="day" stroke={ct.axisLine} tick={{ fontSize: 11, fill: ct.axisTick }} interval={1} />
                    <YAxis stroke={ct.axisLine} tick={{ fontSize: 12, fill: ct.axisTick }} tickFormatter={(v) => compact(v)} />
                    <RechartsTooltip {...tt} />
                    <Line type="monotone" dataKey="users" stroke={CHART_SERIES[3]} strokeWidth={2} dot={{ fill: CHART_SERIES[3], r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick actions + activity */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Quick Actions
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { label: 'Approve KYC', icon: <KycIcon />, path: '/kyc', tone: 'warning' as const },
                  { label: 'Review Payouts', icon: <PayoutIcon />, path: '/payouts', tone: 'error' as const },
                  { label: 'Run Reconciliation', icon: <MoneyIcon />, path: '/reconciliation', tone: 'info' as const },
                  { label: 'Fraud Alerts', icon: <FraudIcon />, path: '/fraud', tone: 'primary' as const },
                  { label: 'Support Queue', icon: <SupportIcon />, path: '/support', tone: 'success' as const },
                  { label: 'Feature Flags', icon: <BoltIcon />, path: '/feature-flags', tone: 'info' as const },
                ].map((a) => (
                  <Grid item xs={6} key={a.label}>
                    <CardActionArea
                      onClick={() => navigate(a.path)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        '&:hover': { borderColor: `${a.tone}.main`, bgcolor: 'action.hover' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: `${a.tone}.main`,
                          color: `${a.tone}.contrastText`,
                          flexShrink: 0,
                        }}
                      >
                        {React.cloneElement(a.icon, { fontSize: 'small' } as any)}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', flex: 1 }}>
                        {a.label}
                      </Typography>
                    </CardActionArea>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Recent Admin Activity
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Immutable audit log (last 8)
                  </Typography>
                </Box>
                <Button size="small" onClick={() => navigate('/audit-logs')} sx={{ textTransform: 'none' }}>
                  View all →
                </Button>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              {activity.isLoading ? (
                <Stack spacing={1.5}>
                  {[...Array(5)].map((_, i) => (
                    <Stack key={i} direction="row" spacing={2} alignItems="center">
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" />
                        <Skeleton width="35%" />
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              ) : (() => {
                const raw: any = activity.data;
                // Backend audit-logs returns { logs, pagination }; legacy /admin/actions returns { actions, pagination };
                // some paginated endpoints use { items } / { data }. Check all variants before giving up.
                const items: any[] = Array.isArray(raw)
                  ? raw
                  : Array.isArray(raw?.items)
                  ? raw.items
                  : Array.isArray(raw?.logs)
                  ? raw.logs
                  : Array.isArray(raw?.actions)
                  ? raw.actions
                  : Array.isArray(raw?.data)
                  ? raw.data
                  : [];
                if (items.length === 0) {
                  return (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No admin activity yet. Actions appear here as admins work.
                      </Typography>
                    </Box>
                  );
                }
                return (
                <Stack spacing={0}>
                  {items.slice(0, 8).map((ev: any, idx: number) => (
                    <React.Fragment key={ev.id ?? idx}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13, fontWeight: 700 }}>
                          {(ev.actorEmail || ev.actorId || 'S').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }} noWrap>
                            <strong>{ev.action || 'action'}</strong>{' '}
                            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                              on {ev.entityType || 'resource'} {ev.entityId ? `#${String(ev.entityId).slice(0, 8)}` : ''}
                            </Typography>
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {ev.actorEmail || ev.actorId || 'system'} · {ev.createdAt ? timeAgo(ev.createdAt) : ''}
                          </Typography>
                        </Box>
                      </Stack>
                      {idx < 7 && <Divider />}
                    </React.Fragment>
                  ))}
                </Stack>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loadingStats && <LinearProgress sx={{ mt: 2 }} />}
    </Box>
  );
};

export default Dashboard;
